import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Loader, truncateText, isSubscriptionActive, shouldShowPaymentAlert, currentYear, formatNumber } from '../../utils/tools';
import PaymentAlert from '../../utils/paymentAlert';
import PlanLimitAlert from '../../utils/planLimitAlert';

import { fetchEvidenceList, deleteEvidenceList } from '../../store/actions/evidenceList';
import { setYear, setQuarter } from '../../store/reducers/uiSlice';

import { IoChevronDownOutline } from 'react-icons/io5';

import { PLAN_LIMITS } from '../../config/plans';

import AddEvidenceSheet from './add';
import EditEvidenceList from './edit';
import Detail from './detail';
import CustomModalLarge from '../../hoc/customModalLarge';
import CustomModalFullscreen from '../../hoc/customModalFullscreen';

import { Table, Button, Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';

// Assets
import { ReactComponent as DocumentIcon } from '../../assets/document.svg';
import { ReactComponent as EditIcon } from '../../assets/edit.svg';
import { ReactComponent as RemoveIcon } from '../../assets/remove.svg';


const CATEGORY_LABELS = {
    'N': 'Nebezpečný odpad',
    'O': 'Ostatný odpad',
};

const EvidenceList = () => {

    const loading = useSelector(state => state.evidenceList.loading);
    const evidences = useSelector(state => state.evidenceList.data);
    const branchId = useSelector(state => state.branch.current);
    const removeEvidenceList = useSelector(state => state.evidenceList.remove);
    const tenant = useSelector(state => state.tenant.current);
    const year = useSelector(state => state.uiSlice.selectedYear);
    const quarter = useSelector(state => state.uiSlice.selectedQuarter);

    const planLimits = PLAN_LIMITS[tenant.plan];
    const isActive = isSubscriptionActive(tenant?.subscriptionStatus, tenant?.plan);
    const isAtLimit = !!(tenant?.plan && evidences && Array.isArray(evidences) && evidences.length >= planLimits?.maxEvidenceLists);

    const dispatch = useDispatch();

    const thisYear = currentYear();
    const years = [thisYear, thisYear - 1, thisYear - 2, thisYear - 3, thisYear - 4];
    const quarters = [
        { key: 0, label: 'Všetky kvartály' },
        { key: 1, label: '1. kvartál' },
        { key: 2, label: '2. kvartál' },
        { key: 3, label: '3. kvartál' },
        { key: 4, label: '4. kvartál' },
    ];

    const [yearOpen, setYearOpen] = useState(false);
    const [quarterOpen, setQuarterOpen] = useState(false);
    const [categoryOpen, setCategoryOpen] = useState(false);
    const [activityOpen, setActivityOpen] = useState(false);
    const yearRef = useRef(null);
    const quarterRef = useRef(null);
    const categoryRef = useRef(null);
    const activityRef = useRef(null);

    const [filterCategory, setFilterCategory] = useState('');
    const [filterActivity, setFilterActivity] = useState('');

    const availableCategories = useMemo(() =>
        [...new Set(Array.isArray(evidences) ? evidences.map(e => e.wasteCode?.wasteCategory).filter(Boolean) : [])].sort(),
        [evidences]
    );
    const availableActivities = useMemo(() =>
        [...new Set(Array.isArray(evidences) ? evidences.map(e => e.activityCode).filter(Boolean) : [])].sort(),
        [evidences]
    );

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (yearRef.current && !yearRef.current.contains(e.target)) setYearOpen(false);
            if (quarterRef.current && !quarterRef.current.contains(e.target)) setQuarterOpen(false);
            if (categoryRef.current && !categoryRef.current.contains(e.target)) setCategoryOpen(false);
            if (activityRef.current && !activityRef.current.contains(e.target)) setActivityOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleYearChange = (y) => {
        dispatch(setYear(y));
        setYearOpen(false);
    };

    const handleQuarterChange = (q) => {
        dispatch(setQuarter(q));
        setQuarterOpen(false);
    };

    useEffect(() => {
        if(branchId !== '')
            dispatch(fetchEvidenceList({ branchId, year, quarter }));
    }, [dispatch, branchId, year, quarter, removeEvidenceList]);

    // Custom modal state
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [evidenceListToEdit, setEvidenceListToEdit] = useState(null);
    const [resetForm, setResetForm] = useState(null);

    const [show, setShow] = useState(false);
    const [selectedEvidence, setSelectedEvidence] = useState(null);
    const [disableRemoveButton, setDisableRemoveButton] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [detailEvidenceId, setDetailEvidenceId] = useState(null);

    // Handlers
    const handleAddEvidenceSheetButton = () => {
        setIsAddModalVisible(true);
    };

    const handleEditButtonClick = (evidenceList) => {
        setEvidenceListToEdit(null);
        setEvidenceListToEdit(evidenceList);
        setIsEditModalVisible(true);
    };


    const handleCloseAddModal = () => {
        setIsAddModalVisible(false);
    };

    const handleClose = () => {
        setShow(false);
        setSelectedEvidence(null);
    }

    const handleCloseEditModal = () => {
        setIsEditModalVisible(false);
        setEvidenceListToEdit(null);
    };

    const handleShow = (evidenceList) => {
        setSelectedEvidence(evidenceList)
        setShow(true);
    }

    // Detail evidenčného listu (Vznik/Nakladanie/Zostatok v tomto zozname) sa dopočítava
    // len pri fetchEvidenceList - kým je otvorený detail, pridané/duplikované/upravené/zmazané
    // odpady tento zoznam neaktualizujú. Preto po zatvorení detailu treba zoznam znovu načítať,
    // inak tu zostanú staré (prípadne chýbajúce) súhrny zobrazené ako "—".
    const handleCloseDetail = () => {
        setDetailEvidenceId(null);
        if (branchId !== '') {
            dispatch(fetchEvidenceList({ branchId, year, quarter }));
        }
    }

    const handleDeleteEvidenceList = (id, name) => {
        dispatch(deleteEvidenceList({id, currentBranch: branchId, name}));
        handleClose();
    }

    // Remove
    useEffect(() => {
        // Check if input name is equal evidence list's name
        if (selectedEvidence && inputValue === selectedEvidence.name) {
          setDisableRemoveButton(false);
        } else {
          setDisableRemoveButton(true);
        }
    }, [inputValue, selectedEvidence]); // Spustí sa pri zmene inputValue alebo selectedEvidence

    const handleChange = (event) => {
        setInputValue(event.target.value);
    };

    const filteredEvidences = Array.isArray(evidences)
        ? evidences.filter((e) => {
            const term = searchTerm.toLowerCase();
            const matchesSearch = (
                e.name?.toLowerCase().includes(term) ||
                e.activityCode?.toLowerCase().includes(term) ||
                e.wasteCode?.wasteCode?.toLowerCase().includes(term) ||
                e.wasteCode?.wasteName?.toLowerCase().includes(term)
            );
            const matchesCategory = !filterCategory || e.wasteCode?.wasteCategory === filterCategory;
            const matchesActivity = !filterActivity || e.activityCode === filterActivity;
            return matchesSearch && matchesCategory && matchesActivity;
        })
        : [];

    // Render Tooltips
    const renderTooltipEnter = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Vstúpiť do evidenčného listu
        </Tooltip>
    );

    const renderTooltipEdit = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Upraviť evidenčný list
        </Tooltip>
    );

    const renderTooltipRemove = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Vymazať evidenčný list
        </Tooltip>
    );

    const renderTooltipFullWasteName = (wasteName) => (props) => (
        <Tooltip id="button-tooltip" {...props}>
            {wasteName}
        </Tooltip>
    );

    return (
        <>  
            {/* Modal */}
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Vymazať evidenčný list</Modal.Title>
                </Modal.Header>
            
                <Modal.Body>
                    {selectedEvidence ? (
                        <div>
                            <p>Ak ste si istý, že chcete vymazať evidenčný list <strong>{selectedEvidence.name}</strong> zadajte meno evidenčného listu pre potvrdenie.</p>
                            <p>Spolu s evidenčným listom sa vymažú aj všetky odpady, ktoré evidenčný list obsahoval!</p>
                            <Form.Label htmlFor="inputPassword5">Meno evidenčného listu</Form.Label>
                            <Form.Control
                                type="text"
                                id="inputName"
                                onChange={handleChange}
                                placeholder="Zadajte meno evidenčného listu"

                            />
                        </div>                  

                    ) : (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '35vh' 
                        }}>
                            <Loader />
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Zavrieť
                    </Button>
                    <Button
                        disabled={disableRemoveButton} 
                        variant="danger" 
                        onClick={() => handleDeleteEvidenceList(selectedEvidence._id, inputValue)}
                    >
                        Vymazať
                    </Button>
                </Modal.Footer>
            </Modal>

            { loading ?
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '35vh' 
                }}>
                    <Loader/>
                </div>
                : 
                <div>

                    <div className="evidence-toolbar">
                        <div className="evidence-toolbar-top">
                            <Button
                                className="button-custom-large radius"
                                onClick={handleAddEvidenceSheetButton}
                                disabled={tenant.plan && evidences && Array.isArray(evidences) && evidences.length >= planLimits.maxEvidenceLists || !isActive}
                            >
                                Pridať evidenčný list
                            </Button>

                            <div className="evidence-search-col">
                                <div className="evidence-search-wrapper">
                                    <span className="evidence-search-icon">
                                        <svg width="15" height="15" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="m19 19-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </span>
                                    <input
                                        type="text"
                                        className="evidence-search-input"
                                        placeholder="Hľadať podľa názvu, kódu alebo odpadu..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button className="evidence-search-clear" onClick={() => setSearchTerm('')} aria-label="Vymazať hľadanie">
                                            ✕
                                        </button>
                                    )}
                                </div>
                                {searchTerm && Array.isArray(evidences) && (
                                    <span className="evidence-search-count">
                                        {filteredEvidences.length} z {evidences.length} výsledkov
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="evidence-toolbar-filters">
                            <span className="evidence-toolbar-filters-label">Filtre</span>
                            <div className="notification-center" ref={categoryRef}>
                                <button className="select-branch-trigger" style={{ width: '170px' }} onClick={() => setCategoryOpen(o => !o)}>
                                    <span>{filterCategory ? CATEGORY_LABELS[filterCategory] ?? filterCategory : 'Kategória'}</span>
                                    <IoChevronDownOutline size={14} className={`select-branch-chevron${categoryOpen ? ' open' : ''}`} />
                                </button>
                                {categoryOpen && (
                                    <div className="notification-dropdown select-branch-dropdown">
                                        <div className="notification-header">Kategória</div>
                                        <button
                                            className={`user-menu-item select-branch-item${filterCategory === '' ? ' active' : ''}`}
                                            onClick={() => { setFilterCategory(''); setCategoryOpen(false); }}
                                        >
                                            Všetky
                                        </button>
                                        {availableCategories.map(cat => (
                                            <button
                                                key={cat}
                                                className={`user-menu-item select-branch-item${filterCategory === cat ? ' active' : ''}`}
                                                onClick={() => { setFilterCategory(cat); setCategoryOpen(false); }}
                                            >
                                                {CATEGORY_LABELS[cat] ?? cat}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="notification-center" ref={activityRef}>
                                <button className="select-branch-trigger" onClick={() => setActivityOpen(o => !o)}>
                                    <span>{filterActivity || 'Činnosť'}</span>
                                    <IoChevronDownOutline size={14} className={`select-branch-chevron${activityOpen ? ' open' : ''}`} />
                                </button>
                                {activityOpen && (
                                    <div className="notification-dropdown select-branch-dropdown">
                                        <div className="notification-header">Činnosť</div>
                                        <button
                                            className={`user-menu-item select-branch-item${filterActivity === '' ? ' active' : ''}`}
                                            onClick={() => { setFilterActivity(''); setActivityOpen(false); }}
                                        >
                                            Všetky
                                        </button>
                                        {availableActivities.map(act => (
                                            <button
                                                key={act}
                                                className={`user-menu-item select-branch-item${filterActivity === act ? ' active' : ''}`}
                                                onClick={() => { setFilterActivity(act); setActivityOpen(false); }}
                                            >
                                                {act}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="notification-center" ref={quarterRef}>
                                <button className="select-branch-trigger" onClick={() => setQuarterOpen(o => !o)}>
                                    <span>{quarter === 0 ? 'Všetky kvartály' : `${quarter}. kvartál`}</span>
                                    <IoChevronDownOutline size={14} className={`select-branch-chevron${quarterOpen ? ' open' : ''}`} />
                                </button>
                                {quarterOpen && (
                                    <div className="notification-dropdown select-branch-dropdown">
                                        <div className="notification-header">Kvartál</div>
                                        {quarters.map(q => (
                                            <button
                                                key={q.key}
                                                className={`user-menu-item select-branch-item${quarter === q.key ? ' active' : ''}`}
                                                onClick={() => handleQuarterChange(q.key)}
                                            >
                                                {q.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="notification-center" ref={yearRef}>
                                <button className="select-branch-trigger" onClick={() => setYearOpen(o => !o)}>
                                    <span>{year}</span>
                                    <IoChevronDownOutline size={14} className={`select-branch-chevron${yearOpen ? ' open' : ''}`} />
                                </button>
                                {yearOpen && (
                                    <div className="notification-dropdown select-branch-dropdown">
                                        <div className="notification-header">Rok</div>
                                        {years.map(y => (
                                            <button
                                                key={y}
                                                className={`user-menu-item select-branch-item${year === y ? ' active' : ''}`}
                                                onClick={() => handleYearChange(y)}
                                            >
                                                {y}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {tenant.plan && evidences && Array.isArray(evidences) && evidences.length >= planLimits.maxEvidenceLists && (
                        <PlanLimitAlert
                            current={evidences.length}
                            max={planLimits.maxEvidenceLists}
                            label="evidenčných listov"
                            plan={tenant.plan}
                        />
                    )}

                    {tenant && !isAtLimit && shouldShowPaymentAlert(tenant.subscriptionStatus, tenant.plan) && (
                        <PaymentAlert status={tenant.subscriptionStatus} plan={tenant.plan} />
                    )}

                    <div className="content pt-4 pb-4">
                        <Table responsive hover size="sm" className="evidence-list-table">
                            <thead>
                                <tr>
                                    <th className="px-3">Názov</th>
                                    <th>Kód</th>
                                    <th className="text-center">Činnosť</th>
                                    <th>Názov odpadu</th>
                                    <th className="text-center">Kategória</th>
                                    <th>Y</th>
                                    <th>Ohlásenie</th>
                                    <th>Vznik</th>
                                    <th>Nakladanie</th>
                                    <th>Zostatok</th>
                                    <th>Akcia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(evidences) && evidences.length === 0 && (
                                    <tr>
                                        <td colSpan={11} className="text-center text-muted py-5">
                                            <p className="mb-1"><strong>Zatiaľ nemáte žiadny evidenčný list.</strong></p>
                                            <p className="mb-0">Kliknutím na tlačidlo <em>Pridať evidenčný list</em> vytvoríte prvý evidenčný list.</p>
                                        </td>
                                    </tr>
                                )}
                                {Array.isArray(evidences) && evidences.length > 0 && filteredEvidences.length === 0 && (
                                    <tr>
                                        <td colSpan={11} className="text-center text-muted py-5">
                                            <p className="mb-0">Žiadny evidenčný list nevyhovuje vyhľadávaniu <em>„{searchTerm}"</em>.</p>
                                        </td>
                                    </tr>
                                )}
                                {filteredEvidences.map((evidenceList) => (
                                    <tr key={evidenceList._id}>
                                        <OverlayTrigger placement="top" overlay={renderTooltipFullWasteName(evidenceList.name)}>
                                            <td className="px-3"><span className="evidence-list-name-cell">{evidenceList.name}</span></td>
                                        </OverlayTrigger>
                                        <td>{evidenceList.wasteCode.wasteCode}</td>
                                        <td className="text-center">{evidenceList.activityCode}</td>
                                        <OverlayTrigger placement="top" overlay={renderTooltipFullWasteName(evidenceList.wasteCode.wasteName)}>
                                            <td><span className="evidence-list-waste-cell">{evidenceList.wasteCode.wasteName}</span></td>
                                        </OverlayTrigger>
                                        <td className="text-center">{evidenceList.wasteCode.wasteCategory}</td>
                                        <td>{evidenceList.yCode || '-'}</td>
                                        <td>
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    backgroundColor: evidenceList.includedInReport !== false ? '#28a745' : '#dc3545',
                                                    marginRight: 6
                                                }}
                                            />
                                            {evidenceList.includedInReport !== false ? 'Áno' : 'Nie'}
                                        </td>
                                        <td>{evidenceList.summary ? new Intl.NumberFormat('sk-SK', { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(formatNumber(evidenceList.summary.income)) : '—'}</td>
                                        <td>{evidenceList.summary ? new Intl.NumberFormat('sk-SK', { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(formatNumber(evidenceList.summary.disposal)) : '—'}</td>
                                        <td>{evidenceList.summary ? new Intl.NumberFormat('sk-SK', { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(formatNumber(Number((evidenceList.summary.income - evidenceList.summary.disposal).toFixed(3)))) : '—'}</td>
                                        <td>

                                            {/* Enter to evidence list */}
                                            <Button
                                                className="btnIcon"
                                                onClick={() => setDetailEvidenceId(evidenceList._id)}
                                            >
                                                <OverlayTrigger placement="top" overlay={renderTooltipEnter}>
                                                    <DocumentIcon
                                                        alt="Vstúpiť"
                                                        className="icon icon-document"
                                                    />
                                                </OverlayTrigger>
                                            </Button>
                                           
                                           {/* Edit Icon */}
                                            <Button
                                                className="btnIcon"
                                            >
                                                <OverlayTrigger placement="top" overlay={renderTooltipEdit}>
                                                    <EditIcon
                                                        onClick={() => handleEditButtonClick(evidenceList)}
                                                        alt="Upraviť"
                                                        className="icon icon-edit"
                                                    />
                                                </OverlayTrigger>
                                            </Button>
                                            
                                            {/* Remove Icon */}
                                            <Button 
                                                className="btnIcon"
                                            >
                                                <OverlayTrigger placement="top" overlay={renderTooltipRemove}>
                                                    <RemoveIcon
                                                        onClick={() => handleShow(evidenceList)}
                                                        alt="Vymazať"
                                                        className="icon icon-remove"
                                                    />
                                                </OverlayTrigger>
                                            </Button>

                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            }

            {/* Custom modal to adding evidence sheet */}
            <CustomModalLarge isVisible={isAddModalVisible} onClose={handleCloseAddModal}>
                <AddEvidenceSheet onClose={handleCloseAddModal} />
            </CustomModalLarge>

            {/* Custom modal to editing evidence sheet */}
            <CustomModalLarge isVisible={isEditModalVisible} onClose={handleCloseEditModal}>
                {
                    evidenceListToEdit && isEditModalVisible ?
                        <EditEvidenceList evidenceList={evidenceListToEdit} onClose={handleCloseEditModal} currentBranch={branchId} />
                        : null
                }
            </CustomModalLarge>

            {/* Fullscreen modal - detail evidenčného listu */}
            <CustomModalFullscreen isVisible={!!detailEvidenceId} onClose={handleCloseDetail}>
                {detailEvidenceId && (
                    <Detail id={detailEvidenceId} onClose={handleCloseDetail} />
                )}
            </CustomModalFullscreen>

        </>
    )
}

export default EvidenceList;